/**
 * Vehicle search sort (Milestone 4).
 */
import {
  normalizeCanonicalSort,
  VEHICLE_SEARCH_SORT,
} from "../../constants/vehicleSearchParamsConstants";
import { resolveRiskLevel } from "../vehicleRiskDisplay";

const FAIR_PRICE_RANK = {
  below_market: 0,
  fair_price: 1,
  slightly_high: 2,
  high_price: 3,
  not_enough_data: 4,
  unknown: 5,
};

function numOrNull(value) {
  if (value == null || Number.isNaN(Number(value))) return null;
  return Number(value);
}

function compareMissingLast(a, b, pick, direction = 1) {
  const av = pick(a);
  const bv = pick(b);
  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;
  return direction * (av - bv);
}

function compareStringDesc(a, b, pick) {
  const av = pick(a);
  const bv = pick(b);
  if (!av && !bv) return 0;
  if (!av) return 1;
  if (!bv) return -1;
  return bv.localeCompare(av);
}

function recommendedScore(listing, keyword) {
  let score = 0;
  if (listing.dealerSearchPriorityScore != null) {
    score += Math.min(50, Number(listing.dealerSearchPriorityScore) / 2);
  }
  if (listing.dealerVerified) score += 12;
  if (listing.qualityScore != null) score += Math.min(30, Number(listing.qualityScore) / 3);
  const fairRank = FAIR_PRICE_RANK[listing.fairPriceLabel] ?? 5;
  score += Math.max(0, 12 - fairRank * 2);
  const risk = resolveRiskLevel(listing);
  if (risk === "low") score += 8;
  if (risk === "unknown") score += 2;
  if (listing.lastConfirmedAt) {
    const age = Date.now() - new Date(listing.lastConfirmedAt).getTime();
    if (!Number.isNaN(age) && age < 7 * 24 * 3_600_000) score += 6;
  }
  if (keyword) {
    const hay = [listing.title, listing.make, listing.model].filter(Boolean).join(" ").toLowerCase();
    if (hay.includes(keyword.toLowerCase())) score += 10;
  }
  return score;
}

export function getDealerDiversityKey(listing) {
  if (listing?.dealerId != null && String(listing.dealerId).trim()) {
    return `id:${String(listing.dealerId).trim()}`;
  }
  if (listing?.dealerSlug) {
    return `slug:${String(listing.dealerSlug).trim().toLowerCase()}`;
  }
  const dealerLabel = listing?.dealerName || listing?.sellerName;
  if (dealerLabel && String(dealerLabel).trim()) {
    return `name:${String(dealerLabel).trim().toLowerCase()}`;
  }
  if (listing?.sourceKey) {
    return `source:${String(listing.sourceKey).trim().toLowerCase()}`;
  }
  return `listing:${listing?.id || listing?.vehicleListingId || "unknown"}`;
}

function breakAdjacentSameDealer(ranked) {
  const result = [...ranked];
  let changed = true;
  let guard = 0;

  while (changed && guard < result.length * 3) {
    changed = false;
    guard += 1;

    for (let i = 1; i < result.length; i += 1) {
      const prevKey = getDealerDiversityKey(result[i - 1]);
      const currKey = getDealerDiversityKey(result[i]);
      if (prevKey === currKey) {
        let bestIdx = -1;
        let bestScore = -1;
        for (let j = i + 1; j < result.length; j += 1) {
          const candidateKey = getDealerDiversityKey(result[j]);
          if (candidateKey === currKey) continue;
          let score = candidateKey !== prevKey ? 3 : 0;
          if (j + 1 < result.length && candidateKey !== getDealerDiversityKey(result[j + 1])) {
            score += 1;
          }
          if (candidateKey !== getDealerDiversityKey(result[j - 1])) {
            score += 1;
          }
          if (score > bestScore) {
            bestScore = score;
            bestIdx = j;
          }
        }
        if (bestIdx !== -1) {
          const [candidate] = result.splice(bestIdx, 1);
          result.splice(i, 0, candidate);
          changed = true;
          break;
        }
      }
    }
  }

  return result;
}

function sortRecommendedWithDealerDiversity(listings, keyword) {
  const scored = listings
    .map((listing, index) => ({
      listing,
      score: recommendedScore(listing, keyword),
      index,
      key: getDealerDiversityKey(listing),
    }))
    .sort((a, b) => b.score - a.score || a.index - b.index);

  const dealerQueues = new Map();
  for (const row of scored) {
    if (!dealerQueues.has(row.key)) {
      dealerQueues.set(row.key, []);
    }
    dealerQueues.get(row.key).push(row);
  }

  const total = scored.length;
  const placements = [];

  for (const [key, rows] of dealerQueues.entries()) {
    const count = rows.length;
    for (let i = 0; i < count; i += 1) {
      const slot = Math.min(
        total - 1,
        Math.floor((i * total) / count + total / (count * 2))
      );
      placements.push({ slot, row: rows[i], key });
    }
  }

  placements.sort((a, b) => a.slot - b.slot || b.row.score - a.row.score || a.row.index - b.row.index);

  const ranked = [];
  const usedSlots = new Set();
  for (const placement of placements) {
    let slot = placement.slot;
    while (usedSlots.has(slot) && slot < total) {
      slot += 1;
    }
    while (usedSlots.has(slot) && slot > 0) {
      slot -= 1;
    }
    usedSlots.add(slot);
    ranked[slot] = placement.row.listing;
  }

  return breakAdjacentSameDealer(ranked.filter(Boolean));
}

export function needsDealerDiversitySort(sort) {
  return normalizeCanonicalSort(sort) === VEHICLE_SEARCH_SORT.RECOMMENDED;
}

export function needsClientSideSort(sort) {
  const canonical = normalizeCanonicalSort(sort);
  return (
    canonical === VEHICLE_SEARCH_SORT.VERIFIED_DEALERS_FIRST ||
    canonical === VEHICLE_SEARCH_SORT.RECENTLY_CONFIRMED ||
    canonical === VEHICLE_SEARCH_SORT.RECOMMENDED ||
    canonical === VEHICLE_SEARCH_SORT.BEST_VALUE
  );
}

/**
 * @param {Array} listings
 * @param {string} sort
 * @param {{ keyword?: string|null }} [context]
 */
export function sortVehicleListings(listings, sort, context = {}) {
  const canonical = normalizeCanonicalSort(sort);
  const items = [...listings];
  const keyword = context.keyword || null;

  switch (canonical) {
    case VEHICLE_SEARCH_SORT.LOWEST_PRICE:
      items.sort((a, b) => compareMissingLast(a, b, (l) => numOrNull(l.priceCop), 1));
      break;
    case VEHICLE_SEARCH_SORT.HIGHEST_PRICE:
      items.sort((a, b) => compareMissingLast(a, b, (l) => numOrNull(l.priceCop), -1));
      break;
    case VEHICLE_SEARCH_SORT.LOWEST_MILEAGE:
      items.sort((a, b) => compareMissingLast(a, b, (l) => numOrNull(l.mileageKm), 1));
      break;
    case VEHICLE_SEARCH_SORT.NEWEST_YEAR:
      items.sort((a, b) => compareMissingLast(a, b, (l) => numOrNull(l.year), -1));
      break;
    case VEHICLE_SEARCH_SORT.NEWEST:
      items.sort((a, b) =>
        compareStringDesc(a, b, (l) => l.publishedAt || l.createdAt || l.updatedAt || null)
      );
      break;
    case VEHICLE_SEARCH_SORT.BEST_VALUE:
      items.sort((a, b) => {
        const ar = FAIR_PRICE_RANK[a.fairPriceLabel] ?? 5;
        const br = FAIR_PRICE_RANK[b.fairPriceLabel] ?? 5;
        if (ar !== br) return ar - br;
        return compareMissingLast(a, b, (l) => numOrNull(l.qualityScore), -1);
      });
      break;
    case VEHICLE_SEARCH_SORT.VERIFIED_DEALERS_FIRST:
      items.sort((a, b) => {
        const av = a.dealerVerified ? 1 : 0;
        const bv = b.dealerVerified ? 1 : 0;
        if (av !== bv) return bv - av;
        return recommendedScore(b, keyword) - recommendedScore(a, keyword);
      });
      break;
    case VEHICLE_SEARCH_SORT.RECENTLY_CONFIRMED:
      items.sort((a, b) =>
        compareStringDesc(a, b, (l) => l.lastConfirmedAt || l.updatedAt || l.publishedAt || null)
      );
      break;
    case VEHICLE_SEARCH_SORT.RECOMMENDED:
    default:
      return sortRecommendedWithDealerDiversity(items, keyword);
  }

  return items;
}
